import { createServerFn } from "@tanstack/react-start";
import { readFileSync } from "node:fs";
import { join } from "node:path";

type FileEntry = {
  filename: string;
  expectedBodyContains?: string;
  expectedBodyStartsWith?: string;
  expectedBodyEquals?: string;
  contentType?: string;
  note?: string;
};

type Result = {
  filename: string;
  url: string;
  ok: boolean;
  status: number | null;
  contentType: string | null;
  error: string | null;
  checkedAt: string;
};

const BASE = "https://vendingmachinefree.com";

function loadEntries(): FileEntry[] {
  try {
    // Read from repo at runtime (bundled into worker filesystem at /scripts)
    const raw = readFileSync(
      join(process.cwd(), "scripts/verification-files.config.json"),
      "utf8",
    );
    const parsed = JSON.parse(raw) as { files?: FileEntry[] };
    return parsed.files ?? [];
  } catch {
    // Fallback to the known Google file so the page is never empty.
    return [
      {
        filename: "google72b9c75a1471797d.html",
        expectedBodyStartsWith:
          "google-site-verification: google72b9c75a1471797d.html",
        contentType: "text/html",
        note: "Google Search Console — HTML file method",
      },
    ];
  }
}

function checkBody(entry: FileEntry, body: string): string | null {
  if (entry.expectedBodyEquals && body.trim() !== entry.expectedBodyEquals.trim()) {
    return `Body does not equal expected value`;
  }
  if (
    entry.expectedBodyStartsWith &&
    !body.trim().startsWith(entry.expectedBodyStartsWith)
  ) {
    return `Body does not start with "${entry.expectedBodyStartsWith}"`;
  }
  if (entry.expectedBodyContains && !body.includes(entry.expectedBodyContains)) {
    return `Body missing substring "${entry.expectedBodyContains}"`;
  }
  return null;
}

async function checkEntry(entry: FileEntry): Promise<Result> {
  const url = `${BASE}/${entry.filename}`;
  const checkedAt = new Date().toISOString();
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10_000);
    const res = await fetch(url, {
      redirect: "follow",
      signal: ctrl.signal,
      headers: { "user-agent": "vendingmachines-verify-status/1.0" },
    });
    clearTimeout(t);
    const body = await res.text();
    const contentType = res.headers.get("content-type");
    if (res.status !== 200) {
      return {
        filename: entry.filename,
        url,
        ok: false,
        status: res.status,
        contentType,
        error: `HTTP ${res.status}`,
        checkedAt,
      };
    }
    const bodyErr = checkBody(entry, body);
    if (bodyErr) {
      return {
        filename: entry.filename,
        url,
        ok: false,
        status: res.status,
        contentType,
        error: bodyErr,
        checkedAt,
      };
    }
    return {
      filename: entry.filename,
      url,
      ok: true,
      status: res.status,
      contentType,
      error: null,
      checkedAt,
    };
  } catch (err) {
    return {
      filename: entry.filename,
      url,
      ok: false,
      status: null,
      contentType: null,
      error: err instanceof Error ? err.message : String(err),
      checkedAt,
    };
  }
}

// Module-level cache of the last successful check (best-effort; resets on
// worker cold-start, which is fine — the daily CI run reseeds it).
let lastSuccessAt: string | null = null;

type NoindexProbe = {
  url: string;
  ok: boolean;
  status: number | null;
  xRobotsTag: string | null;
  metaRobots: string | null;
  error: string | null;
};

async function probeSelfNoindex(): Promise<NoindexProbe> {
  const url = `${BASE}/verification-status`;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10_000);
    const res = await fetch(url, {
      redirect: "follow",
      signal: ctrl.signal,
      headers: { "user-agent": "vendingmachines-noindex-probe/1.0" },
    });
    clearTimeout(t);
    const html = await res.text();
    const xRobotsTag = res.headers.get("x-robots-tag");
    const metaMatch = html.match(
      /<meta[^>]+name=["']robots["'][^>]+content=["']([^"']+)["']/i,
    );
    const metaRobots = metaMatch ? metaMatch[1] : null;
    const noindexInMeta = !!metaRobots && /noindex/i.test(metaRobots);
    const noindexInHeader = !!xRobotsTag && /noindex/i.test(xRobotsTag);
    return {
      url,
      ok: noindexInMeta || noindexInHeader,
      status: res.status,
      xRobotsTag,
      metaRobots,
      error:
        noindexInMeta || noindexInHeader
          ? null
          : "No noindex directive found in meta tag or X-Robots-Tag header",
    };
  } catch (err) {
    return {
      url,
      ok: false,
      status: null,
      xRobotsTag: null,
      metaRobots: null,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

export const getVerificationStatus = createServerFn({ method: "GET" }).handler(
  async () => {
    const entries = loadEntries();
    const [results, noindex] = await Promise.all([
      Promise.all(entries.map(checkEntry)),
      probeSelfNoindex(),
    ]);
    const allOk = results.length > 0 && results.every((r) => r.ok);
    if (allOk) lastSuccessAt = new Date().toISOString();
    return {
      checkedAt: new Date().toISOString(),
      lastSuccessAt,
      baseUrl: BASE,
      results,
      noindex,
    };
  },
);
